import type { NuxtApp } from '#app';
import {
  CATALOG_MORPHS_FETCH_ERROR,
  CATALOG_DISTRICTS_FETCH_ERROR,
} from '@/pages/catalog/lib/catalogMessages';

import { ROOT_CATALOG_URL } from '@/shared/constants/urlConstants';
import { useLocationStore } from '@/entities/location/store/location';
import { useCategoriesStore } from '@/entities/category/store/category';

import type { SeoLinkItem, SeoTab } from '@/pages/catalog/model/types/seoLinks';
import type { CatalogMeta } from '@/pages/catalog/model/types/useCatalog';
import type {
  DistrictRaw,
  DistrictsResponse,
} from '@/widgets/Filters/model/types/useLocationFilter';
import type {
  CategoryMorph,
  CityMorph,
  FilterType,
  LinkType,
  MorphType,
  Morphs,
  RawItem,
  SeoLinksData,
  UseCatalogSeoLinksReturn,
} from '@/pages/catalog/model/types/useCatalogSeoLinks';

import {
  FIRST_TAB_VALUE,
  SECOND_TAB_VALUE,
  THIRD_TAB_VALUE,
  DEFAULT_ITEMS_NUMBER,
} from '@/shared/constants/seoLinksConstants';
import { useErrorHandler } from '@/shared/lib/errors';

const CITIES_LIMIT_DEFAULT = 39;
const CITIES_LIMIT_WIDE = 54;

const useCatalogSeoLinks = (): UseCatalogSeoLinksReturn => {
  const { handleError } = useErrorHandler();
  const route = useRoute();
  const locationStore = useLocationStore();
  const categoriesStore = useCategoriesStore();
  const { cities } = storeToRefs(locationStore);
  const { categories } = storeToRefs(categoriesStore);
  const { $morphService, $locationService } = useNuxtApp();

  const meta = inject<Ref<CatalogMeta>>('meta', ref({}));

  const isMobile: boolean = useIsMobile();
  const columnsCount: ComputedRef<number> = computed(() => (isMobile ? 15 : 10));
  const countInColumn: ComputedRef<number> = computed(() => (isMobile ? 2 : 4));
  const indexOfLastItem: Ref<number> = ref(columnsCount.value * countInColumn.value - 1);
  const activeTab: Ref<string> = ref(FIRST_TAB_VALUE);

  const morphs: ComputedRef<Morphs> = computed(
    () =>
      seoData.value?.morphs ?? {
        city: {},
        activity_categories: {},
        activity_categories_sub: {},
      },
  );
  const districtsDefaultArray: ComputedRef<DistrictRaw[]> = computed(
    () => seoData.value?.district ?? [],
  );
  const metroDefaultArray: ComputedRef<DistrictRaw[]> = computed(
    () => seoData.value?.metro ?? [],
  );

  const region: ComputedRef<string | undefined> = computed(() =>
    route.params.region?.toString(),
  );
  const category: ComputedRef<string | undefined> = computed(() =>
    route.params.category?.toString(),
  );
  const subcategory: ComputedRef<string | undefined> = computed(() =>
    route.params.subcategory?.toString(),
  );
  const districtQuery: ComputedRef<string | undefined> = computed(() =>
    route.query.district?.toString(),
  );
  const metroQuery: ComputedRef<string | undefined> = computed(() =>
    route.query.metro?.toString(),
  );

  const isCatalogDefault: ComputedRef<boolean> = computed(
    () => !region.value && !category.value && !subcategory.value,
  );
  const isOnlyRegion: ComputedRef<boolean> = computed(
    () => Boolean(region.value) && !category.value && !subcategory.value,
  );
  const isOnlyCategory: ComputedRef<boolean> = computed(
    () => Boolean(category.value) && !region.value && !subcategory.value,
  );
  const isCategorySubcategory: ComputedRef<boolean> = computed(
    () => Boolean(category.value) && Boolean(subcategory.value) && !region.value,
  );
  const isRegionCategory: ComputedRef<boolean> = computed(
    () => Boolean(region.value) && Boolean(category.value) && !subcategory.value,
  );

  const cityId: ComputedRef<number | undefined> = computed(() => meta.value?.city?.id);
  const categoryId: ComputedRef<number | undefined> = computed(
    () => meta.value?.category?.id,
  );
  const subcategoryId: ComputedRef<number | undefined> = computed(
    () => meta.value?.subcategory?.id,
  );

  const categoryDative: ComputedRef<string> = computed(
    () => morphs.value.activity_categories?.singular_dative?.toLowerCase() ?? '',
  );
  const subcategoryDative: ComputedRef<string> = computed(
    () => morphs.value.activity_categories_sub?.singular_dative?.toLowerCase() ?? '',
  );
  const seoLinksKey = computed(
    () =>
      `catalog-seo-links-${cityId.value ?? ''}-${categoryId.value ?? ''}-${subcategoryId.value ?? ''}`,
  );

  const filterType: ComputedRef<FilterType> = computed(() => {
    if (isCatalogDefault.value) return 'default';
    if (isOnlyRegion.value) return 'onlyRegion';
    if (isOnlyCategory.value) return 'onlyCategory';
    if (isCategorySubcategory.value) return 'categorySubcategory';
    if (isRegionCategory.value) return 'regionCategory';
    return 'regionCategorySubcategory';
  });

  const buildLink = (el: RawItem, linkType: LinkType): string => {
    const reg = region.value;
    const cat = category.value;
    const sub = subcategory.value;
    const slug = el?.slug ?? '';
    const name = el?.name ?? '';

    const links: Record<FilterType, Partial<Record<LinkType, string>>> = {
      default: {
        city: `${ROOT_CATALOG_URL}geo/${slug}/`,
        category: `${ROOT_CATALOG_URL}${slug}/`,
      },
      onlyRegion: {
        category: `${ROOT_CATALOG_URL}geo/${reg}/${slug}/`,
        district: `${ROOT_CATALOG_URL}geo/${reg}/?district=${name}`,
        metro: `${ROOT_CATALOG_URL}geo/${reg}/?metro=${name}`,
      },
      onlyCategory: {
        city: `${ROOT_CATALOG_URL}geo/${slug}/${cat}/`,
        subcategory: `${ROOT_CATALOG_URL}${cat}/${slug}/`,
      },
      categorySubcategory: {
        city: `${ROOT_CATALOG_URL}geo/${slug}/${cat}/${sub}/`,
        subcategory: `${ROOT_CATALOG_URL}${cat}/${slug}/`,
      },
      regionCategory: {
        subcategory: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/${slug}/`,
        district: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/?district=${name}`,
        metro: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/?metro=${name}`,
      },
      regionCategorySubcategory: {
        subcategory: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/${slug}/`,
        district: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/${sub}/?district=${name}`,
        metro: `${ROOT_CATALOG_URL}geo/${reg}/${cat}/${sub}/?metro=${name}`,
      },
    };

    return links[filterType.value][linkType] ?? '';
  };

  const mapSeoLink = (el: RawItem, index: number, linkType: LinkType): SeoLinkItem => ({
    id: el?.id ?? 0,
    slug: el?.slug ?? '',
    name: el?.name ?? el?.text ?? '',
    text: el?.name ?? el?.text ?? '',
    link: buildLink(el, linkType),
    subwayColor: el?.city_id ? (el?.color ?? el?.subwayColor ?? '') : '',
    subcategories: el?.slug === category.value ? (el?.subcategories ?? []) : [],
    classMod: index > indexOfLastItem.value ? '_hide' : '',
  });

  const visibleCities: ComputedRef<RawItem[]> = computed(() =>
    isCatalogDefault.value
      ? cities.value.slice(0, CITIES_LIMIT_DEFAULT)
      : cities.value.slice(0, CITIES_LIMIT_WIDE),
  );

  const citiesList: ComputedRef<SeoLinkItem[]> = computed(() => {
    const list = visibleCities.value.map((city, index) =>
      mapSeoLink(city, index, 'city'),
    );
    return list;
  });

  const categoriesList: ComputedRef<SeoLinkItem[]> = computed(() => {
    const list = categories.value
      .map((categoryItem, index) => mapSeoLink(categoryItem, index, 'category'))
      .filter((link) => link.slug !== category.value);
    return list;
  });

  const subcategoriesList: ComputedRef<SeoLinkItem[]> = computed(() => {
    const currentCategory = categories.value.find(
      (categoryItem) => categoryItem.slug === category.value,
    );
    return (currentCategory?.subcategories ?? [])
      .map((subcategoryItem: RawItem, index: number) =>
        mapSeoLink(subcategoryItem, index, 'subcategory'),
      )
      .filter((link: SeoLinkItem) => link.slug !== subcategory.value);
  });

  const districtsList: ComputedRef<SeoLinkItem[]> = computed(() =>
    districtsDefaultArray.value
      .map((district, index) => mapSeoLink(district, index, 'district'))
      .filter((link) => link.name !== districtQuery.value),
  );

  const metroList: ComputedRef<SeoLinkItem[]> = computed(() =>
    metroDefaultArray.value
      .map((metro, index) => mapSeoLink(metro, index, 'metro'))
      .filter((link) => link.name !== metroQuery.value),
  );

  const fetchMorph = async (
    type: MorphType,
    id: number | undefined,
  ): Promise<CityMorph | CategoryMorph> => {
    if (!id) return {};
    try {
      const response = await $morphService.getMorphById<CityMorph>(type, id, {});
      return response?.data ?? {};
    } catch (e) {
      handleError(e, { context: { action: CATALOG_MORPHS_FETCH_ERROR } });
      return {};
    }
  };

  const loadAllPlaces = async (
    id: number,
  ): Promise<{ district: DistrictRaw[]; metro: DistrictRaw[] }> => {
    try {
      const { data } = await $locationService.getAllDistricts<{
        data: DistrictsResponse;
      }>({ city_id: id, is_used: 1 });

      return { district: data.district, metro: data.metro };
    } catch (error) {
      handleError(error, { context: { action: CATALOG_DISTRICTS_FETCH_ERROR } });
      return { district: [], metro: [] };
    }
  };

  const requiredMorphsByFilterType: Record<FilterType, MorphType[]> = {
    default: [],
    onlyRegion: ['city'],
    onlyCategory: ['activity_categories'],
    categorySubcategory: ['activity_categories_sub'],
    regionCategory: ['city', 'activity_categories'],
    regionCategorySubcategory: ['city'],
  };

  const firstTabItems: ComputedRef<SeoLinkItem[]> = computed(() => {
    if (isCatalogDefault.value) return citiesList.value;
    if (isOnlyRegion.value) return categoriesList.value;
    if (isOnlyCategory.value || isCategorySubcategory.value) return citiesList.value;
    return subcategoriesList.value;
  });

  const secondTabItems: ComputedRef<SeoLinkItem[]> = computed(() => {
    if (isCatalogDefault.value) return categoriesList.value;
    if (isOnlyRegion.value) return districtsList.value;
    if (isOnlyCategory.value || isCategorySubcategory.value) {
      return subcategoriesList.value;
    }
    return districtsList.value;
  });

  const thirdTabItems: ComputedRef<SeoLinkItem[]> = computed(() => metroList.value);

  const tabList: ComputedRef<SeoTab[]> = computed(() => {
    const tabs: SeoTab[] = [];
    if (isCatalogDefault.value) {
      tabs.push({ id: 1, label: 'Юристы в городах России', value: FIRST_TAB_VALUE });
      tabs.push({ id: 2, label: 'По специализациям', value: SECOND_TAB_VALUE });
      return tabs;
    }
    if (isOnlyRegion.value || districtQuery.value || metroQuery.value) {
      tabs.push({
        id: 1,
        label: `Юристы ${morphs.value.city?.singular_genitive ?? ''} по специализациям`,
        value: FIRST_TAB_VALUE,
      });
      if (secondTabItems.value.length) {
        tabs.push({ id: 2, label: 'В районах', value: SECOND_TAB_VALUE });
      }
      if (metroList.value.length) {
        tabs.push({ id: 3, label: 'У ст. метро', value: THIRD_TAB_VALUE });
      }
      return tabs;
    }
    if (isOnlyCategory.value) {
      tabs.push({
        id: 1,
        label: `Юристы по ${categoryDative.value} в городах`,
        value: FIRST_TAB_VALUE,
      });
      tabs.push({
        id: 2,
        label: `Услуги юристов по ${categoryDative.value}`,
        value: SECOND_TAB_VALUE,
      });
      return tabs;
    }
    if (isCategorySubcategory.value) {
      tabs.push({
        id: 1,
        label: `Юристы по ${subcategoryDative.value} в городах`,
        value: FIRST_TAB_VALUE,
      });
      tabs.push({
        id: 2,
        label: 'По сопутствующим услугам',
        value: SECOND_TAB_VALUE,
      });
      return tabs;
    }
    if (isRegionCategory.value) {
      tabs.push({
        id: 1,
        label: `Услуги юристов по ${categoryDative.value} в ${morphs.value.city?.singular_prepositional ?? ''}`,
        value: FIRST_TAB_VALUE,
      });
      if (secondTabItems.value.length) {
        tabs.push({ id: 2, label: 'В районах', value: SECOND_TAB_VALUE });
      }
      if (metroList.value.length) {
        tabs.push({ id: 3, label: 'У ст. метро', value: THIRD_TAB_VALUE });
      }
      return tabs;
    }
    tabs.push({
      id: 1,
      label: `Юристы ${morphs.value.city?.singular_genitive ?? ''} по сопутствующим услугам`,
      value: FIRST_TAB_VALUE,
    });
    if (secondTabItems.value.length) {
      tabs.push({ id: 2, label: 'В районах', value: SECOND_TAB_VALUE });
    }
    if (metroList.value.length) {
      tabs.push({ id: 3, label: 'У ст. метро', value: THIRD_TAB_VALUE });
    }
    return tabs;
  });

  const showMoreItems = (currentTab: string | number): void => {
    if (activeTab.value !== String(currentTab)) return;
    indexOfLastItem.value += DEFAULT_ITEMS_NUMBER;
  };

  const changeTab = (value: string | number): void => {
    activeTab.value = String(value);
    indexOfLastItem.value = columnsCount.value * countInColumn.value - 1;
  };

  const initData = async (): Promise<SeoLinksData> => {
    const needed = requiredMorphsByFilterType[filterType.value];

    const idByType: Record<MorphType, number | undefined> = {
      city: cityId.value,
      activity_categories: categoryId.value,
      activity_categories_sub: subcategoryId.value,
    };

    const loadedMorphs: Morphs = {
      city: {},
      activity_categories: {},
      activity_categories_sub: {},
    };

    await Promise.all(
      needed.map(async (type) => {
        loadedMorphs[type] = await fetchMorph(type, idByType[type]);
      }),
    );
    const places = cityId.value
      ? await loadAllPlaces(cityId.value)
      : { district: [], metro: [] };

    return { morphs: loadedMorphs, district: places.district, metro: places.metro };
  };

  const { data: seoData, status } = useAsyncData(seoLinksKey, () => initData(), {
    watch: [cityId, categoryId, subcategoryId],
    getCachedData: (key: string, nuxtApp: NuxtApp) =>
      nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
  });

  const isLoading: ComputedRef<boolean> = computed(() => status.value === 'pending');

  return {
    isLoading,
    activeTab,
    tabList,
    firstTabItems,
    secondTabItems,
    thirdTabItems,
    columnsCount,
    countInColumn,
    indexOfLastItem,
    isCatalogDefault,
    changeTab,
    showMoreItems,
  };
};

export default useCatalogSeoLinks;
